import { Injectable } from '@angular/core';
import { BehaviorSubject } from 'rxjs';
import { BackendStorageService } from './backend-storage.service';

@Injectable({
  providedIn: 'root'
})
export class ThemeService {
  private isDarkModeSubject = new BehaviorSubject<boolean>(false);
  public isDarkMode$ = this.isDarkModeSubject.asObservable();
  private initialized = false;

  constructor(private backendStorage: BackendStorageService) {
    this.initializeTheme();
  }

  private async initializeTheme(): Promise<void> {
    try {
      // Try to get theme from backend storage
      const themePrefs = await this.backendStorage.getThemePreferences().toPromise();
      const isDark = themePrefs?.mode === 'dark';
      this.setDarkMode(isDark, false); // Don't save back to backend
      this.initialized = true;
    } catch (error) {
      // Fallback to localStorage for migration or offline scenarios
      console.warn('Backend theme loading failed, using localStorage fallback:', error);
      const savedTheme = localStorage.getItem('theme');
      const prefersDark = window.matchMedia('(prefers-color-scheme: dark)').matches;
      const isDark = savedTheme ? savedTheme === 'dark' : prefersDark;
      
      this.setDarkMode(isDark, false);
      this.initialized = true;
      
      // Try to migrate to backend storage
      if (savedTheme) {
        this.migrateThemeToBackend(isDark);
      }
    }
  }

  private async migrateThemeToBackend(isDark: boolean): Promise<void> {
    try {
      await this.backendStorage.updateThemePreferences({ mode: isDark ? 'dark' : 'light' }).toPromise();
      // Clear localStorage after successful migration
      localStorage.removeItem('theme');
    } catch (error) {
      console.warn('Theme migration to backend failed:', error);
    }
  }

  toggleTheme(): void {
    this.setDarkMode(!this.isDarkModeSubject.value);
  }

  setDarkMode(isDark: boolean, saveToBackend: boolean = true): void {
    this.isDarkModeSubject.next(isDark);
    
    // Apply theme to DOM
    if (isDark) {
      document.body.classList.add('dark-theme');
      document.body.classList.remove('light-theme');
    } else {
      document.body.classList.add('light-theme');
      document.body.classList.remove('dark-theme');
    }

    // Save to backend storage if initialized and requested
    if (saveToBackend && this.initialized) {
      this.backendStorage.updateThemePreferences({ mode: isDark ? 'dark' : 'light' }).subscribe({
        error: (error) => {
          console.error('Failed to save theme to backend:', error);
          // Fallback to localStorage in case of backend failure
          localStorage.setItem('theme', isDark ? 'dark' : 'light');
        }
      });
    }
  }

  get isDarkMode(): boolean {
    return this.isDarkModeSubject.value;
  }

  // Method to ensure theme is loaded (useful for app initialization)
  async waitForInitialization(): Promise<void> {
    if (this.initialized) {
      return;
    }

    return new Promise((resolve) => {
      const checkInitialized = () => {
        if (this.initialized) {
          resolve();
        } else {
          setTimeout(checkInitialized, 50);
        }
      };
      checkInitialized();
    });
  }
}